import { Vue, Component } from 'vue-property-decorator';
import { createLabel, getBrowserMetadata } from '@/features/shipment/helpers/createShipment';
import { handle3dsCardLookup, redirectTo3dsChallenge, CardLookupError } from '@/features/shipment/helpers/threeDsHelper';
import {
  AvailableService,
  CarrierLabelResult,
  InProgress,
  ThreeDsCardLookupDto,
} from '@/features/shipment/types';
import { trackCreateFormSubmit, trackPaymentComplete, trackShipmentCompleted } from '@/features/common/helpers/tracked-events';

@Component
export class CheckoutCreateLabelMixin extends Vue {
  public redirectUrl = '';

  public oopsErrorMessage: string | null | undefined = '';

  public syncedShow = false;

  public labelResponse: CarrierLabelResult | null = null;

  public carrierMessage = false;

  get selectedService(): AvailableService | null {
    return this.$store.getters.getSelectedService;
  }

  public async createLabelAndPrint(): Promise<void> {
    if (!this.selectedService) {
      this.showOopsDialog(this.$t('checkout.noServiceAvailable') as string, false);
      return;
    }

    let threeDsData: ThreeDsCardLookupDto | null = null;
    try {
      threeDsData = await handle3dsCardLookup();
    } catch (error) {
      if (error instanceof CardLookupError) {
        this.showOopsDialog(error.errorMessage, false);
        return;
      }
      // If it's not a CardLookupError, rethrow it
      throw error;
    }

    const metaData = getBrowserMetadata(threeDsData?.cardLookupId);

    this.labelResponse = await createLabel(true, metaData);

    if (this.labelResponse && this.labelResponse.successful) {
      this.$router.replace({
        name: 'shipment-print',
        query: { id: this.labelResponse.secureshipTrackingNo },
      });
      this.$store.dispatch('clearInProgressDataInShortly');
      this.$store.dispatch('clearEstimateDataInShortly');
      this.$store.dispatch('refetchUserInfo');

      const inProgress: InProgress = this.$store.getters.getInProgress;
      const fromCountry = inProgress?.fromAddress?.countryCode || '';
      const toCountry = inProgress?.toAddress?.countryCode || '';
      const isIntl = fromCountry !== toCountry;
      const carrier = this.selectedService?.carrierCode || '';
      const total = this.selectedService?.total || 0;

      trackCreateFormSubmit(this.$gtag, {
        shipmentType: isIntl ? 'intl' : 'domestic',
        carrier: carrier.toLowerCase(),
        originCountry: fromCountry,
        destinationCountry: toCountry,
      });

      trackPaymentComplete(this.$gtag, {
        value: total,
        currency: 'CAD',
        paymentMethod: 'credit_card',
      });

      trackShipmentCompleted(this.$gtag, {
        shipmentId: this.labelResponse.secureshipTrackingNo,
        value: total,
      });
    } else if (this.labelResponse?.errorType === 'CreditCardChallengeRequired' && this.labelResponse?.threeDsChallenge) {
      await redirectTo3dsChallenge(this.labelResponse.threeDsChallenge);
    } else {
      this.redirectUrl = this.labelResponse?.redirectUrl ?? '';
      this.showOopsDialog(
        this.labelResponse?.errorMessage,
        this.labelResponse?.isCarrierMessage,
      );
    }
  }

  public showOopsDialog(
    errorMessage: string | null | undefined,
    isCarrierMessage: boolean | undefined,
  ): void {
    this.oopsErrorMessage = errorMessage || this.$t('alertDialog.somethingWentWrong') as string;
    this.carrierMessage = !!isCarrierMessage;
    this.syncedShow = true;
  }
}
